import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Target, Plus, Trash2, Edit2, CheckCircle2, RefreshCw,
  Loader2, TrendingUp, Award, Calendar, Lightbulb
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getCategoryColor = (category) => {
  const colors = {
    'Growth': 'bg-emerald-100 text-emerald-700',
    'Quality': 'bg-blue-100 text-blue-700',
    'Efficiency': 'bg-violet-100 text-violet-700',
    'Financial': 'bg-amber-100 text-amber-700'
  };
  return colors[category] || 'bg-gray-100 text-gray-700';
};

export default function GoalTracker() {
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newGoal, setNewGoal] = useState({
    metric: '',
    target_value: '',
    current_value: '',
    deadline: '',
    notes: ''
  });

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('qapi-token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    const headers = { 'Authorization': `Bearer ${token}` };
    
    try {
      const [goalsRes, suggestionsRes] = await Promise.all([
        fetch(`${API_URL}/api/quick-wins/goals`, { headers }),
        fetch(`${API_URL}/api/quick-wins/suggested-goals`, { headers })
      ]);
      
      if (goalsRes.ok) {
        const data = await goalsRes.json();
        setGoals(data.goals || []);
      }
      
      if (suggestionsRes.ok) {
        const data = await suggestionsRes.json();
        setSuggestions(data.suggested_goals || []);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchData, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleAddGoal = async () => {
    const token = localStorage.getItem('qapi-token');
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/quick-wins/goals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          metric: newGoal.metric,
          target_value: parseFloat(newGoal.target_value),
          current_value: parseFloat(newGoal.current_value) || 0,
          deadline: newGoal.deadline || null,
          notes: newGoal.notes || null
        })
      });
      
      if (res.ok) {
        setShowAddDialog(false);
        setNewGoal({ metric: '', target_value: '', current_value: '', deadline: '', notes: '' });
        fetchData();
      }
    } catch (error) {
      console.error('Error adding goal:', error);
    }
  };

  const handleUpdateGoal = async (goalId, updates) => {
    const token = localStorage.getItem('qapi-token');
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/quick-wins/goals/${goalId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      fetchData();
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    const token = localStorage.getItem('qapi-token');
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/quick-wins/goals/${goalId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      fetchData();
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  const addSuggestedGoal = (suggestion) => {
    setNewGoal({
      metric: suggestion.metric,
      target_value: suggestion.suggested_target.toString(),
      current_value: suggestion.current_value.toString(),
      deadline: '',
      notes: suggestion.description
    });
    setShowAddDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading goals...</span>
      </div>
    );
  }

  const completedGoals = goals.filter(g => g.progress >= 100);
  const activeGoals = goals.filter(g => g.progress < 100);

  return (
    <div className="space-y-6 p-6" data-testid="goal-tracker-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-7 w-7 text-violet-500" />
            Goal Tracker
          </h1>
          <p className="text-muted-foreground">Set and track your operational goals</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Goal
          </Button>
          <Button onClick={fetchData} variant="outline" size="icon">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Total Goals</p>
            <p className="text-3xl font-bold">{goals.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-3xl font-bold text-blue-600">{activeGoals.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Completed</p>
            <p className="text-3xl font-bold text-emerald-600">{completedGoals.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Completion Rate</p>
            <p className="text-3xl font-bold text-violet-600">
              {goals.length > 0 ? Math.round(completedGoals.length / goals.length * 100) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Goals */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-semibold">Active Goals</h2>
          {activeGoals.length > 0 ? (
            <div className="space-y-3">
              {activeGoals.map((goal, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold">{goal.metric}</h3>
                        {goal.notes && (
                          <p className="text-sm text-muted-foreground">{goal.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteGoal(goal.goal_id)}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Current: {goal.current_value}</span>
                          <span>Target: {goal.target_value}</span>
                        </div>
                        <Progress value={goal.progress || 0} className="h-2" />
                      </div>
                      <Badge className={goal.progress >= 75 ? 'bg-emerald-100 text-emerald-700' : 
                                        goal.progress >= 50 ? 'bg-blue-100 text-blue-700' : 
                                        'bg-amber-100 text-amber-700'}>
                        {goal.progress || 0}%
                      </Badge>
                    </div>
                    
                    {goal.deadline && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        Deadline: {goal.deadline}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Target className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No active goals yet</p>
                <Button onClick={() => setShowAddDialog(true)} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Goal
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Completed Goals */}
          {completedGoals.length > 0 && (
            <>
              <h2 className="text-lg font-semibold mt-6 flex items-center gap-2">
                <Award className="h-5 w-5 text-yellow-500" />
                Completed Goals
              </h2>
              <div className="space-y-3">
                {completedGoals.map((goal, idx) => (
                  <Card key={idx} className="bg-emerald-50/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                          <span className="font-medium">{goal.metric}</span>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Suggested Goals */}
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Suggested Goals
          </h2>
          <div className="space-y-3">
            {suggestions.map((suggestion, idx) => (
              <Card key={idx} className="cursor-pointer hover:border-primary transition-colors"
                    onClick={() => addSuggestedGoal(suggestion)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-sm">{suggestion.metric}</h4>
                    <Badge className={getCategoryColor(suggestion.category)}>
                      {suggestion.category}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{suggestion.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span>Current: {suggestion.current_value}</span>
                    <span className="font-medium">Target: {suggestion.suggested_target}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Add Goal Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Goal</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Metric Name</label>
              <Input
                value={newGoal.metric}
                onChange={(e) => setNewGoal({ ...newGoal, metric: e.target.value })}
                placeholder="e.g., ADC (Census)"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Current Value</label>
                <Input
                  type="number"
                  value={newGoal.current_value}
                  onChange={(e) => setNewGoal({ ...newGoal, current_value: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Target Value</label>
                <Input
                  type="number"
                  value={newGoal.target_value}
                  onChange={(e) => setNewGoal({ ...newGoal, target_value: e.target.value })}
                  placeholder="100"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Deadline (optional)</label>
              <Input
                type="date"
                value={newGoal.deadline}
                onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Input
                value={newGoal.notes}
                onChange={(e) => setNewGoal({ ...newGoal, notes: e.target.value })}
                placeholder="Description or notes about this goal"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddGoal} disabled={!newGoal.metric || !newGoal.target_value}>
              Create Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
